import React, { useState, useEffect } from 'react';
import { useAsset } from '../../context/AssetContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { 
  Monitor, CheckCircle, Wrench, UserCheck, AlertTriangle, 
  ShieldOff, Clock, Package
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from 'recharts';

const COLORS = ['#22c55e', '#8b5cf6', '#f97316', '#ef4444', '#6b7280'];

const AssetDashboard = ({ onNavigate }) => {
  const { getDashboardStats, getStatusDistribution, isReady } = useAsset();
  
  const [stats, setStats] = useState(null);
  const [distribution, setDistribution] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isReady) {
      loadDashboard();
    }
  }, [isReady]);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const [statsData, distData] = await Promise.all([
        getDashboardStats(),
        getStatusDistribution()
      ]);
      setStats(statsData);
      setDistribution(distData || []);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    }
    setLoading(false);
  };

  const statCards = [
    { key: 'total', label: 'Total Assets', icon: Package, color: 'bg-blue-500', textColor: 'text-blue-600', filters: {} },
    { key: 'available', label: 'Available', icon: CheckCircle, color: 'bg-green-500', textColor: 'text-green-600', filters: { status: 'Available' } },
    { key: 'in_repair', label: 'In Repair', icon: Wrench, color: 'bg-orange-500', textColor: 'text-orange-600', filters: { status: 'In Repair,With Vendor for Repair' } },
    { key: 'allocated', label: 'Allocated', icon: UserCheck, color: 'bg-purple-500', textColor: 'text-purple-600', filters: { status: 'Allocated' } },
    { key: 'end_of_life', label: 'End of Life', icon: AlertTriangle, color: 'bg-red-500', textColor: 'text-red-600', filters: { status: 'End of Life,Retired' } },
    { key: 'out_of_warranty', label: 'Out of Warranty', icon: ShieldOff, color: 'bg-yellow-500', textColor: 'text-yellow-600', filters: { warranty_filter: 'expired' } },
    { key: 'warranty_expiring_soon', label: 'Warranty Expiring Soon', icon: Clock, color: 'bg-amber-500', textColor: 'text-amber-600', filters: { warranty_filter: 'expiring_soon' } },
  ];

  const pieData = distribution.map((item, index) => ({
    name: item.status || 'Unknown',
    value: item.count || 0,
    color: COLORS[index % COLORS.length]
  }));

  const barData = stats ? [
    { name: 'Total', value: stats.total || 0, fill: '#3b82f6' },
    { name: 'Available', value: stats.available || 0, fill: '#22c55e' },
    { name: 'In Repair', value: stats.in_repair || 0, fill: '#f97316' },
    { name: 'Allocated', value: stats.allocated || 0, fill: '#8b5cf6' },
  ] : [];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          <Skeleton className="h-80" />
          <Skeleton className="h-80" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="asset-dashboard">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Dashboard</h2>
        <p className="text-muted-foreground">Asset overview and statistics</p>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
        {statCards.map((card) => (
          <Card 
            key={card.key} 
            className="cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => onNavigate('assets', card.filters)}
            data-testid={`stat-${card.key}`}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{card.label}</p>
                  <p className={`text-2xl font-bold ${card.textColor}`}>
                    {stats?.[card.key] ?? 0}
                  </p>
                </div>
                <div className={`p-2 rounded-full ${card.color} bg-opacity-10`}>
                  <card.icon className={`h-5 w-5 ${card.textColor}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Pie Chart - Status Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Asset Status Distribution</CardTitle>
            <CardDescription>Breakdown by current status</CardDescription>
          </CardHeader>
          <CardContent>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                    onClick={(entry) => entry?.name && onNavigate('assets', { status: entry.name })}
                    style={{ cursor: 'pointer' }}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Bar Chart - Asset Metrics */}
        <Card>
          <CardHeader>
            <CardTitle>Asset Metrics</CardTitle>
            <CardDescription>Key asset counts</CardDescription>
          </CardHeader>
          <CardContent>
            {barData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={barData}>
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {barData.map((entry, index) => (
                      <Cell key={`bar-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AssetDashboard;
